import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

import { Document, DocumentType } from '../generated-api';
import useOcrPageDefinition from '../hooks/useOcrPageDefinition';
import { EditModalEntity } from '../page/OcrResult/defs/types';
import DocumentEditModal from '../page/OcrResult/DocumentEdit/DocumentEditModal';
import useDocumentListStore from '../store/useDocumentListStore';

interface EditModalContextType {
  open: (documentId: string) => void;
}

const EditModalContext = createContext<EditModalContextType>({
  open: () => {
    /* nop */
  },
});

type Props = PropsWithChildren<{
  groupId: string;
  type: DocumentType;
}>;

export default function useEditModalContext() {
  const { open } = useContext(EditModalContext);
  return { open };
}

export function EditModalContextProvider({ children, groupId, type }: Props) {
  // モーダルを開いたときに最初に表示する documentId を保持してモーダルの状態を制御する
  // モーダルの「次へ」「前へ」を押しても変化しない
  const [initialDocumentId, setInitialDocumentId] = useState<string>();

  const { actions, ocrResults } = useDocumentListStore(groupId);

  const def = useOcrPageDefinition(type);

  const modalEntities = useMemo<EditModalEntity<unknown>[] | null>(() => {
    if (def.entityToModalEntities) {
      return def.entityToModalEntities(ocrResults ?? []);
    } else {
      return (
        ocrResults?.map((item) => ({
          documentId: item.document_id,
          fileName: item.parsed_path.fullFileName,
          entity: item.latest_revision,
          groupId: item.group_id,
          pageNumber: item.page ?? 0,
        })) ?? null
      );
    }
  }, [def, ocrResults]);

  const [currentDocumentId, setCurrentDocumentId] = useState<string>();

  const open = useCallback(
    (documentId: string) => {
      const exists = modalEntities?.some((item) => item.documentId === documentId);
      if (!exists) {
        // ここには来ない想定だが、万が一起きても一応動くように Error は throw しない
        console.error(`[ModalContext]: Unexpected document(documentId=${documentId})`);
        return;
      }
      setCurrentDocumentId(documentId);
      setInitialDocumentId(documentId);
    },
    [modalEntities]
  );

  const currentIndex = useMemo(() => {
    if (!currentDocumentId || !modalEntities) {
      return -1;
    }
    return modalEntities.findIndex((item) => item.documentId === currentDocumentId);
  }, [currentDocumentId, modalEntities]);

  const currentEntity = useMemo(() => {
    if (currentIndex === -1 || !modalEntities) {
      return null;
    }
    return modalEntities[currentIndex];
  }, [currentIndex, modalEntities]);

  const nextDocumentId = useMemo(() => {
    if (currentIndex === -1 || !modalEntities) {
      return null;
    }
    return modalEntities[currentIndex + 1]?.documentId ?? null;
  }, [currentIndex, modalEntities]);

  const prevDocumentId = useMemo(() => {
    if (currentIndex === -1 || !modalEntities) {
      return null;
    }
    return modalEntities[currentIndex - 1]?.documentId ?? null;
  }, [currentIndex, modalEntities]);

  const onSaveDocument = useCallback(async (_document: Document) => {
    // nop
  }, []);

  const onClose = useCallback(
    (_documentId: string | undefined) => {
      setInitialDocumentId(undefined);
      setCurrentDocumentId(undefined);
      actions.fetchDocumentList();
    },
    [actions]
  );

  const value: EditModalContextType = useMemo(
    () => ({
      open,
      onSaveDocument,
    }),
    [onSaveDocument, open]
  );

  return (
    <EditModalContext.Provider value={value}>
      {children}
      {modalEntities && initialDocumentId && currentEntity && (
        <DocumentEditModal
          currentIndex={currentIndex}
          documentId={currentEntity.documentId}
          entity={currentEntity}
          groupId={groupId}
          hasNext={nextDocumentId != null}
          hasPrev={prevDocumentId != null}
          onClickNext={() => {
            if (nextDocumentId) {
              setCurrentDocumentId(nextDocumentId);
            }
          }}
          onClickPrev={() => {
            if (prevDocumentId) {
              setCurrentDocumentId(prevDocumentId);
            }
          }}
          onClose={onClose}
          onSaveButtonClick={onSaveDocument}
        />
      )}
    </EditModalContext.Provider>
  );
}
